/**
 * Run N-5 — stale bundle at login, DOM-free.
 *
 * An SPA already loaded never re-requests its JS; the hourly VersionNudge
 * (Set D N2) is deliberately a dismissible card, never an auto-reload, because
 * a reload mid-assessment can lose work. A LOGIN is the one moment nothing is
 * in flight, so there the rule flips: on a successful login, if the server's
 * bundle differs from the running one, reload BEFORE the dashboard renders.
 *
 * Rules:
 *  - never block a login on the check (fetch failure / no bundle → 'unknown');
 *  - never loop: remember the served bundle we reloaded for; if we come back
 *    still running the old code (a proxy cache), let the nudge handle it.
 */
export interface StaleLoginDeps {
  running: string | null;
  fetchServed: () => Promise<string | null | undefined>;
  reload: () => void;
  marker: { get(): string | null; set(v: string): void };
}

export type StaleLoginVerdict = 'reloaded' | 'fresh' | 'unknown' | 'already-reloaded';

export async function reloadIfStaleAtLogin(d: StaleLoginDeps): Promise<StaleLoginVerdict> {
  if (!d.running) return 'unknown';
  let served: string | null | undefined;
  try { served = await d.fetchServed(); } catch { return 'unknown'; }
  if (!served) return 'unknown';
  if (served === d.running) return 'fresh';
  if (d.marker.get() === served) return 'already-reloaded';
  d.marker.set(served);
  d.reload();
  return 'reloaded';
}
